import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme, useTheme } from '@material-ui/core';
import { CompetenceDto, IUsersStatistics, UserDto } from 'utils/wemble-api.generated';

const useStyles = makeStyles((theme) => ({
  overview: {
    backgroundColor: 'white',
    borderRadius: 16,
    height: '100%',
    padding: 10,
  },
  overview__table: {
    '& .MuiTableCell-root': {
      borderBottomColor: theme.palette.gray.g6,
      padding: '4px',
    },
    '& .MuiTableCell-head': {
      fontSize: 12,
      textTransform: 'uppercase',
      color: theme.palette.gray.g10,
    },
    '& .MuiTableCell-body': {
      fontWeight: 500,
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-last-child(1) > td': {
      border: 'none',
    },
  },
  overview__title: {
    padding: '10px 10px 0',
    fontSize: 14,
    fontWeight: 900,
    color: theme.palette.gray.g1,
  },
  viewAllButton: {
    backgroundColor: theme.palette.gray.g6,
    padding: '8px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 500,
    fontSize: 14,
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.gray.g9,
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
}));

function workloadToStatus(workload: number) {
  return Math.floor(Math.round(workload * 10) / 10 + 0.5);
}

function statusColor(status: number, theme: Theme) {
  switch (status) {
    case 0:
      return theme.palette.blue.b2;
    case 1:
      return theme.palette.green.g4;
    case 2:
      return theme.palette.yellow.y3;
    case 3:
      return theme.palette.red.r1;
    default:
      return theme.palette.state.disabled;
  }
}

function getInitials(name: string) {
  const nameSplit = name.toUpperCase().split(' ');
  const nameLength = nameSplit.length;
  if (nameLength > 1) {
    return [nameSplit[0].substring(0, 1), nameSplit[nameLength - 1].substring(0, 1)];
  } else if (nameLength === 1) {
    return [nameSplit[0].substring(0, 1), ''];
  } else return '';
}

const CompetenceOverview = ({
  data,
  competences,
  users,
  showMore,
}: {
  data: IUsersStatistics['competenceWorkload'];
  competences: CompetenceDto[];
  users: UserDto[];
  showMore?: () => void;
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div className={classes.overview}>
      <div>
        <Table className={classes.overview__table} size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '15%' }} align={'left'}>
                Avg. workload
              </TableCell>
              <TableCell style={{ width: '50%' }} align={'left'}>
                Competence
              </TableCell>
              <TableCell style={{ width: '35%' }} align={'right'}>
                Members
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(data)
              ?.sort(([, a], [, b]) => (b?.nbrOfUsers ?? 0) - (a?.nbrOfUsers ?? 0))
              .splice(0, 5)
              .map(([competenceId, statistics]) => {
                const usersWithCompetence = users.filter(user =>
                  user.skills?.some(skill =>
                    typeof skill !== 'string' &&
                    skill.competence &&
                    (typeof skill.competence === 'string' ? skill.competence : skill.competence._id) === competenceId
                  )
                );
                const displayUsers = usersWithCompetence.slice(0, 3);
                const remainingCount = usersWithCompetence.length - displayUsers.length;

                return (
                  <TableRow key={`competence-${competenceId}`} style={{ cursor: 'default' }}>
                    <TableCell style={{ width: '15%' }} align={'left'}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: statusColor(workloadToStatus(statistics.avgWorkload ?? 0), theme),
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {statistics.avgWorkload && !isNaN(statistics.avgWorkload)
                          ? (statistics.avgWorkload + 1).toFixed(1)
                          : '-'}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '50%', fontSize: 14, fontWeight: 500 }} align={'left'}>
                      {competences.find((c) => c._id === competenceId)?.name}
                    </TableCell>
                    <TableCell style={{ width: '35%' }} align={'right'}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {displayUsers.map((user, idx) => (
                          <div
                            key={user._id}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: '#efefef',
                              border: '2px solid white',
                              marginLeft: idx > 0 ? -12 : 0,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          >
                            {user.profilePicture ? (
                              <img
                                alt=""
                                src={
                                  'https://d378wr66tg5qg7.cloudfront.net/' + user.profilePicture
                                }
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <span>
                                {getInitials(user.name ?? '')[0]}
                                {getInitials(user.name ?? '')[1]}
                              </span>
                            )}
                          </div>
                        ))}
                        {remainingCount > 0 && (
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.gray.g6,
                              border: '2px solid white',
                              marginLeft: -12,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 600,
                              color: theme.palette.gray.g2,
                            }}
                          >
                            +{remainingCount}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
        <div className={classes.overview__title}>Competency overview</div>
        {showMore && (
          <div onClick={showMore} className={classes.viewAllButton}>
            View all
          </div>
        )}
      </div>
    </div>
  );
};
export default CompetenceOverview;
